import React from 'react';
import { X, MapPin, Tent, Trash2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AutonavCandidate } from '../services/autonavCandidatesService';

interface ReviewCandidatesModalProps {
  candidates: AutonavCandidate[];
  onClose: () => void;
  /** Åpne full gjennomgang (AddSpotModal) for kandidaten. */
  onReview: (candidate: AutonavCandidate) => void;
  /** Forkast kandidaten uten å lagre. */
  onDiscard: (candidateId: string) => void;
}

// Enkelt satellitt-thumbnail for et koordinat (ArcGIS World Imagery, zoom 16).
function satelliteThumbUrl(lat: number, lon: number, zoom = 16): string {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`;
}

function formatWhen(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.round(diff / 60000);
  if (min < 60) return `${Math.max(1, min)} min siden`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} t siden`;
  const d = Math.round(h / 24);
  return `${d} d siden`;
}

const ReviewCandidatesModal: React.FC<ReviewCandidatesModalProps> = ({ candidates, onClose, onReview, onDiscard }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-[9998] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[var(--bg-surface)] w-full md:max-w-md md:rounded-2xl rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-[var(--border-primary)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tent size={16} className="text-emerald-500" />
            <h2 className="text-sm font-black uppercase tracking-widest text-[var(--text-primary)]">
              {t('autonav.review.title', 'Fricamp til gjennomgang')}
            </h2>
            <span className="text-2xs font-bold bg-emerald-500/15 text-emerald-600 px-2 py-0.5 rounded-full">
              {candidates.length}
            </span>
          </div>
          <button onClick={onClose} aria-label="Lukk" className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <p className="px-4 pt-3 text-tiny text-[var(--text-muted)]">
          {t('autonav.review.intro', 'Punkter du markerte mens du kjørte. Gå gjennom hver enkelt for å justere plassering og legge til info før du eventuelt deler den.')}
        </p>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {candidates.length === 0 && (
            <div className="text-center py-10 text-[var(--text-muted)] text-tiny">
              {t('autonav.review.empty', 'Ingen kandidater igjen.')}
            </div>
          )}
          {candidates.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-2 rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface-alt)]">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-stone-800 shrink-0 relative">
                <img
                  src={satelliteThumbUrl(c.location.latitude, c.location.longitude)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <MapPin size={16} className="text-red-500 drop-shadow" fill="#ef4444" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-tiny font-bold text-[var(--text-primary)]">
                  {c.location.latitude.toFixed(5)}, {c.location.longitude.toFixed(5)}
                </div>
                <div className="text-3xs text-[var(--text-muted)] mt-0.5">{formatWhen(c.createdAt)}</div>
              </div>
              <button
                onClick={() => onDiscard(c.id)}
                aria-label={t('autonav.review.discard', 'Forkast')}
                className="p-2 rounded-lg text-[var(--text-muted)] hover:text-red-500 hover:bg-red-500/10 transition-colors shrink-0"
              >
                <Trash2 size={16} />
              </button>
              <button
                onClick={() => onReview(c)}
                className="px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-2xs font-black uppercase tracking-widest transition-all active:scale-95 flex items-center gap-1 shrink-0"
              >
                {t('autonav.review.review', 'Gjennomgå')} <ChevronRight size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewCandidatesModal;
